import { LineWeightRounded, PlayArrow } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, Grid, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { StandardWallDrawerProps } from '../../../@types/ContextProviderRelated';
import { EDirection, EUnitType, EWallInput } from '../../../@types/Enums';
import { useAttributeActions } from '../../../hooks/useAttributeActions';
import AttributeModel, { IAttribute } from '../../../models/standard/AttributeModel';
import PhysicalBoundaryModel from '../../../models/standard/PhysicalBoundary';
import RoomModel from '../../../models/standard/RoomModel';
import StandardWallModel, { IStandardWall } from '../../../models/standard/StandardWallModel';
import { useBuilding } from '../../../provider/BuildingProvider/BuildingProvider';
import { usePlan } from '../../../provider/PlanProvider';
import { useSelection } from '../../../provider/SelectionProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import { getDefaultObjectId, ObjectType } from '../../../utils/attributes/defaultObjectsAndAttributes';
import { deleteButton, immersightBlue, immersightButton } from '../../../utils/colors';
import {
  MAX_WALL_DEPTH,
  MAX_WALL_DEPTH_IMPERIAL,
  MAX_WALL_LENGTH,
  MAX_WALL_LENGTH_IMPERIAL,
  MIN_WALL_DEPTH,
  MIN_WALL_DEPTH_IMPERIAL,
  MIN_WALL_LENGTH,
  MIN_WALL_LENGTH_IMPERIAL,
  UNIT_TO_METER_DIVISOR
} from '../../../utils/constants';
import { cmToInternalRounded, getLocalizedUnitLabel, inchToInternalRounded, internalToCentimetersRounded, internalToInchesRounded } from '../../../utils/conversionUtils';
import filesApiService from '../../../utils/filesApiService';
import { handleWheelInput } from '../../../utils/handleMouseEvents';
import { collectBuildingEdgesForWallHelper, computeSpaceAreaInSquareMeters, pickOppositeSideSpaceRefs } from '../../../utils/multiroomUtils';
import ConfirmationDialog from '../../modals/ConfirmationDialog';
import AttributeAutocomplete from '../AttributeAutocomplete';
import AttributeList from '../AttributeList';
import MultiDirectionalNumberInput from '../MultiDirectionalNumberInput';

const StandardWallDrawer = ({ open, onClose, relatedWallIndex, resetSelection, id }: StandardWallDrawerProps): JSX.Element => {
  const { updateWallThickness, updateWallLength, addWallNode, deleteWall, updateStandardWallAttributes, building, defaultObjects } = useBuilding();

  const wallIndex: number = useMemo(() => (relatedWallIndex !== undefined ? relatedWallIndex : 0), [relatedWallIndex]);

  const wall = useMemo<IStandardWall | undefined>(() => {
    const w = building.walls?.[wallIndex];
    return w instanceof StandardWallModel ? (w as IStandardWall) : undefined;
  }, [building.walls, wallIndex]);

  const [attributes, setAttributes] = useState<AttributeModel[]>(wall?.attributes ?? []);
  const [attributeValues, setAttributeValues] = useState<Record<number, string>>({});
  const [showInputFields, setShowInputFields] = useState<Record<number, boolean>>({});
  const [allAttributes, setAllAttributes] = useState<IAttribute[]>([]);
  const [attrSelected, setAttrSelected] = useState<IAttribute | null>(null);
  const [attrInputValue, setAttrInputValue] = useState('');
  const [attrError, setAttrError] = useState<string | null>(null);

  const { handleAttributeValueChange, handleDeleteAttribute, toggleInputField, handleAttributeSelection, getSortedAttributes } = useAttributeActions(
    attributes,
    setAttributes,
    setAttributeValues,
    setShowInputFields,
    setAttrSelected,
    setAttrInputValue,
    (attrs: AttributeModel[]) => updateStandardWallAttributes(wallIndex, attrs),
    allAttributes,
    setAllAttributes,
    setAttrError,
  );

  const { unitType } = useUnitContext();
  const { setWallChangeDirection } = useSelection();
  const { planModel } = usePlan();
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';
  const intl = useIntl();
  const cmLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'centimeter'), [intl]);
  const inLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'inch'), [intl]);

  type MergeTargets = { a: { roomIdx: number; name: string; area: number }; b: { roomIdx: number; name: string; area: number } };

  const mergeTargets = useMemo<MergeTargets | null>(() => {
    if (!wall || !building?.rooms?.length) return null;
    const refs = collectBuildingEdgesForWallHelper(building, wall.uuid);
    if (!refs.length) return null;
    const pair = pickOppositeSideSpaceRefs(refs);
    if (!pair.a || !pair.b) return null;

    const roomA = building.rooms[pair.a.roomIdx] as RoomModel;
    const roomB = building.rooms[pair.b.roomIdx] as RoomModel;
    const spaceA = roomA.spaces[pair.a.spaceIdx];
    const spaceB = roomB.spaces[pair.b.spaceIdx];
    if (!spaceA || !spaceB) return null;

    const areaA = computeSpaceAreaInSquareMeters(spaceA);
    const areaB = computeSpaceAreaInSquareMeters(spaceB);
    const nameFallback = (idx: number) => intl.formatMessage({ id: 'WallDrawer.roomGeneric', defaultMessage: 'Raum {index}' }, { index: idx + 1 });

    return {
      a: { roomIdx: pair.a.roomIdx, name: roomA.title?.trim() || nameFallback(pair.a.roomIdx), area: areaA },
      b: { roomIdx: pair.b.roomIdx, name: roomB.title?.trim() || nameFallback(pair.b.roomIdx), area: areaB },
    };
  }, [wall, building, intl]);

  const handleMergeIntoA = useCallback(() => {
    if (!mergeTargets?.a) return;
    deleteWall(wallIndex, { behavior: 'mergeRooms', targetRoomIdx: mergeTargets.a.roomIdx });
    resetSelection();
    onClose();
    setDialogMode('NONE');
  }, [mergeTargets, deleteWall, wallIndex, resetSelection, onClose]);

  const handleMergeIntoB = useCallback(() => {
    if (!mergeTargets?.b) return;
    deleteWall(wallIndex, { behavior: 'mergeRooms', targetRoomIdx: mergeTargets.b.roomIdx });
    resetSelection();
    onClose();
    setDialogMode('NONE');
  }, [mergeTargets, deleteWall, wallIndex, resetSelection, onClose]);

  const activeInnerLengthMeters = useMemo(() => {
    if (!wall || !building?.rooms) return wall?.sideALength ?? 0;

    let edgeA: any = null;
    let edgeB: any = null;

    outerLoop: for (const r of building.rooms) {
      for (const s of r.spaces) {
        for (const b of s.boundaries) {
          if (b instanceof PhysicalBoundaryModel) {
            const foundA = b.edges.find((e: any) => e.wall.uuid === wall.uuid && e.side === 'A');
            if (foundA) {
              edgeA = foundA;
              break outerLoop;
            }

            const foundB = b.edges.find((e: any) => e.wall.uuid === wall.uuid && e.side === 'B');
            if (!edgeB && foundB) {
              edgeB = foundB;
            }
          }
        }
      }
    }

    const targetEdge = edgeA ?? edgeB;

    if (targetEdge && targetEdge.startPoint && targetEdge.endPoint) {
      return targetEdge.startPoint.distanceTo(targetEdge.endPoint) / UNIT_TO_METER_DIVISOR;
    }

    return wall.sideALength ?? 0;
  }, [wall, building]);

  // Float precision fix
  const initialWallLengthRaw = parseFloat(activeInnerLengthMeters.toFixed(4)) * UNIT_TO_METER_DIVISOR;
  const initialWallLength = activeInnerLengthMeters ? internalToCentimetersRounded(initialWallLengthRaw) : MIN_WALL_LENGTH;
  const initialWallLengthImperial = activeInnerLengthMeters ? internalToInchesRounded(initialWallLengthRaw) : MIN_WALL_LENGTH_IMPERIAL;

  const initialThickness = wall?.depth ? internalToCentimetersRounded(wall.depth) : MIN_WALL_DEPTH;
  const initialThicknessImperial = wall?.depth ? internalToInchesRounded(wall.depth) : MIN_WALL_DEPTH_IMPERIAL;

  const [wallLength, setWallLength] = useState<number>(0);
  const [inputWallThickness, setInputWallThickness] = useState<number>(0);
  const [dialogMode, setDialogMode] = useState<'NONE' | 'MERGE'>('NONE');

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const catalog = await filesApiService.getAttributes(regionCode);
        setAllAttributes(catalog);
        setAttrError(null);
      } catch (e) {
        setAttrError(`Error fetching attributes: ${(e as Error).message}`);
      }
    })();
  }, [open, regionCode]);

  useEffect(() => {
    if (!wall) {
      if (open) onClose();
      return;
    }

    setAttributes(wall.attributes ?? []);
    setAttributeValues(
      (wall.attributes ?? []).reduce(
        (acc, a) => {
          if (a.value) acc[a.id] = a.value;
          return acc;
        },
        {} as Record<number, string>,
      ),
    );
    setShowInputFields(
      (wall.attributes ?? []).reduce(
        (acc, a) => {
          acc[a.id] = !!a.value;
          return acc;
        },
        {} as Record<number, boolean>,
      ),
    );

    const engineLenRaw = parseFloat(activeInnerLengthMeters.toFixed(4)) * UNIT_TO_METER_DIVISOR;

    if (unitType === EUnitType.METRIC) {
      const engineLen = engineLenRaw ? internalToCentimetersRounded(engineLenRaw) : MIN_WALL_LENGTH;
      setWallLength((prev) => (Math.abs(prev - engineLen) > 0.5 ? engineLen : prev));

      const engineThick = wall.depth ? internalToCentimetersRounded(wall.depth) : MIN_WALL_DEPTH;
      setInputWallThickness((prev) => (Math.abs(prev - engineThick) > 0.5 ? engineThick : prev));
    } else {
      const engineLenIn = engineLenRaw ? internalToInchesRounded(engineLenRaw) : MIN_WALL_LENGTH_IMPERIAL;
      setWallLength((prev) => (Math.abs(prev - engineLenIn) > 0.5 ? engineLenIn : prev));

      const engineThickIn = wall.depth ? internalToInchesRounded(wall.depth) : MIN_WALL_DEPTH_IMPERIAL;
      setInputWallThickness((prev) => (Math.abs(prev - engineThickIn) > 0.5 ? engineThickIn : prev));
    }
  }, [wall, unitType, open, onClose, activeInnerLengthMeters]);

  useEffect(() => {
    setWallChangeDirection(EDirection.BOTH);
  }, [wallIndex, setWallChangeDirection]);

  const validateAndSet = useCallback(
    (value: number, type: EWallInput, direction: EDirection = EDirection.BOTH): void => {
      let maxValue, minValue;
      if (unitType === EUnitType.METRIC) {
        if (type === EWallInput.LENGTH) {
          minValue = MIN_WALL_LENGTH;
          maxValue = MAX_WALL_LENGTH;
        } else {
          minValue = MIN_WALL_DEPTH;
          maxValue = MAX_WALL_DEPTH;
        }
      } else {
        if (type === EWallInput.LENGTH) {
          minValue = MIN_WALL_LENGTH_IMPERIAL;
          maxValue = MAX_WALL_LENGTH_IMPERIAL;
        } else {
          minValue = MIN_WALL_DEPTH_IMPERIAL;
          maxValue = MAX_WALL_DEPTH_IMPERIAL;
        }
      }

      if (!isNaN(value)) {
        if (value < minValue) value = minValue;
        else if (value > maxValue) value = maxValue;
        const roundedInternalValue = unitType === EUnitType.METRIC ? cmToInternalRounded(value) : inchToInternalRounded(value);

        if (type === EWallInput.DEPTH) {
          setInputWallThickness(value);
          updateWallThickness(wallIndex, roundedInternalValue);
        } else {
          const engineDirection = direction === EDirection.LEFT ? EDirection.RIGHT : direction === EDirection.RIGHT ? EDirection.LEFT : EDirection.BOTH;
          setWallChangeDirection(direction);
          setWallLength(value);
          updateWallLength(wallIndex, roundedInternalValue, engineDirection);
        }
      } else if (type === EWallInput.DEPTH) {
        setInputWallThickness(unitType === EUnitType.METRIC ? initialThickness : initialThicknessImperial);
      } else if (type === EWallInput.LENGTH) {
        setWallLength(unitType === EUnitType.METRIC ? initialWallLength : initialWallLengthImperial);
      }
    },
    [unitType, updateWallThickness, updateWallLength, wallIndex, setWallChangeDirection, initialThickness, initialThicknessImperial, initialWallLength, initialWallLengthImperial],
  );

  const handleWallLengthChange = useCallback((val: number, dir: EDirection) => {
    if (Math.abs(val - wallLength) < 0.1) {
      setWallChangeDirection(dir);
      return;
    }
    validateAndSet(val, EWallInput.LENGTH, dir);
  }, [wallLength, validateAndSet, setWallChangeDirection]);

const handleWallThicknessChange = (val: number) => validateAndSet(val, EWallInput.DEPTH, EDirection.BOTH);

  const handleCloseDrawer = useCallback(() => {
    resetSelection();
    onClose();
  }, [onClose, resetSelection]);

  const isCurrentWallShared = useCallback((): boolean => {
    if (!wall) return false;
    let hasA = false,
      hasB = false;
    building.rooms?.forEach((r) =>
      r.spaces?.forEach((s) =>
        s.boundaries?.forEach((b) => {
          if (b instanceof PhysicalBoundaryModel)
            b.edges?.forEach((e) => {
              if (e.wall.uuid === wall.uuid) e.side === 'A' ? (hasA = true) : (hasB = true);
            });
        }),
      ),
    );
    return hasA && hasB;
  }, [building.rooms, wall]);

  const handleOnDelete = useCallback(() => {
    if (!wall) return;
    if (isCurrentWallShared()) {
      if (mergeTargets && mergeTargets.a.roomIdx === mergeTargets.b.roomIdx) {
        deleteWall(wallIndex, { behavior: 'justWall' });
        resetSelection();
        onClose();
        return;
      }
      setDialogMode('MERGE');
    } else {
      deleteWall(wallIndex, { behavior: 'justWall' });
      resetSelection();
      onClose();
    }
  }, [isCurrentWallShared, wall, mergeTargets, deleteWall, wallIndex, resetSelection, onClose]);

  const handleConfirm = useCallback(() => {
    if (dialogMode === 'MERGE') deleteWall(wallIndex, { behavior: 'mergeRooms' });
    resetSelection();
    onClose();
    setDialogMode('NONE');
  }, [deleteWall, wallIndex, dialogMode, resetSelection, onClose]);

  const handleCancel = useCallback(() => setDialogMode('NONE'), []);

  const localizedWallName = useMemo(() => {
    const wallObjId = getDefaultObjectId(ObjectType.Wall);
    const apiObj = defaultObjects?.find((o: any) => o.id === wallObjId);
    if (apiObj && apiObj.localization) {
      const currentLocale = intl.locale.split('-')[0];
      return apiObj.localization[currentLocale] || apiObj.localization.en || apiObj.name;
    }
    return intl.formatMessage({ id: 'WallDrawer.wall', defaultMessage: 'Wand' });
  }, [defaultObjects, intl.locale, intl]);

  return (
    <>
      <Drawer
        key={id}
        anchor="right"
        open={open}
        variant={open ? 'permanent' : 'temporary'}
        sx={{ '& .MuiPaper-root': { top: '64px', zIndex: 1000, width: '420px', height: 'calc(100% - 64px)' } }}
      >
        <Grid container direction="column" p={0}>
          <Grid item sx={{ backgroundColor: immersightBlue, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 0.5 }}>
            <Box sx={{ marginLeft: '16px' }}>
              <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
                {localizedWallName}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDrawer} sx={{ color: '#fff', '&:hover': { backgroundColor: 'transparent' } }}>
              <PlayArrow sx={{ fontSize: 30 }} />
            </IconButton>
          </Grid>
        </Grid>

        <Grid container item direction="column" spacing={3} p={3}>
          <Grid item>
            <Typography variant="subtitle2" gutterBottom style={{ color: '#4a4a4a', letterSpacing: '0.5px' }}>
              <FormattedMessage id="WallDrawer.wallLength" values={{ unit: unitType === EUnitType.METRIC ? cmLocalizedLabel : inLocalizedLabel }} />
            </Typography>
            <MultiDirectionalNumberInput
              value={wallLength}
              min={unitType === EUnitType.METRIC ? MIN_WALL_LENGTH : MIN_WALL_LENGTH_IMPERIAL}
              max={unitType === EUnitType.METRIC ? MAX_WALL_LENGTH : MAX_WALL_LENGTH_IMPERIAL}
              direction={EDirection.BOTH}
              onChange={handleWallLengthChange}
              tooltip={intl.formatMessage({ id: 'MultiDirectionalNumberInput.changeMode' })}
            />
          </Grid>

          <Grid item>
            <Typography variant="subtitle2" style={{ color: '#4a4a4a', letterSpacing: '0.5px', marginBottom: 0 }}>
              <FormattedMessage id="WallDrawer.wallThickness" values={{ unit: unitType === EUnitType.METRIC ? cmLocalizedLabel : inLocalizedLabel }} />
            </Typography>

            <TextField
              fullWidth
              type="number"
              value={inputWallThickness}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                if (!isNaN(val)) setInputWallThickness(val);
              }}
              onBlur={() => handleWallThicknessChange(inputWallThickness)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleWallThicknessChange(inputWallThickness);
                  (e.target as HTMLInputElement).blur();
                }
              }}
              onWheel={(e) => handleWheelInput(e as any, inputWallThickness.toString(), (v) => {
                const val = parseFloat(v);
                if (!isNaN(val)) {
                  setInputWallThickness(val);
                  handleWallThicknessChange(val);
                }
              }, {
                unitType: unitType,
                minMetric: MIN_WALL_DEPTH,
                maxMetric: MAX_WALL_DEPTH,
                minInches: MIN_WALL_DEPTH_IMPERIAL,
                maxInches: MAX_WALL_DEPTH_IMPERIAL,
              })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ padding: '.6rem', borderRadius: '50%', margin: '.5rem', width: '1rem', height: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#757575' }}>
                    <LineWeightRounded sx={{ fontSize: '1.2rem' }} />
                  </InputAdornment>
                ),
                inputProps: { 
                  min: unitType === EUnitType.METRIC ? MIN_WALL_DEPTH : MIN_WALL_DEPTH_IMPERIAL, 
                  max: unitType === EUnitType.METRIC ? MAX_WALL_DEPTH : MAX_WALL_DEPTH_IMPERIAL 
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': { paddingLeft: 0 }
              }}
            />
          </Grid>

          <Grid item>
            <AttributeList
              attributes={getSortedAttributes(attributes)}
              attributeValues={attributeValues}
              showInputFields={showInputFields}
              onAttributeValueChange={handleAttributeValueChange}
              onAttributeUnitChange={(attrId, unit) => {
                const updated = attributes.map((a) =>
                  a.id === attrId
                    ? new AttributeModel(
                        a.id,
                        a.key,
                        a.value,
                        a.predecessorId,
                        a.type,
                        unit,
                        a.hidden,
                        a.forced,
                        a.ownerId,
                        a.userId,
                        a.publicFlag,
                        a.localization,
                        a.defaults,
                        a.validation,
                        a.unitFamily,
                      )
                    : a,
                );
                setAttributes(updated);
                updateStandardWallAttributes(wallIndex, updated);
              }}
              onDeleteAttribute={handleDeleteAttribute}
              onToggleInputField={toggleInputField}
              onReorderAttributes={(newAttrs) => {
                setAttributes(newAttrs);
                updateStandardWallAttributes(wallIndex, newAttrs);
              }}
            />
          </Grid>

          <Grid item>
            <Box sx={{ mt: -5, mb: 1, width: '99%', mx: 'auto' }}>
              <AttributeAutocomplete
                attributes={allAttributes}
                disabledAttributeIds={attributes.map((attribute) => attribute.id)}
                selected={attrSelected}
                inputValue={attrInputValue}
                error={attrError}
                onChange={handleAttributeSelection}
                onInputChange={(val) => {
                  setAttrInputValue(val);
                  if (val !== attrSelected?.key) setAttrSelected(null);
                }}
                placeholder={intl.formatMessage({ id: 'AttributeAutocomplete.addAttribute', defaultMessage: 'Add attribute' })}
              />
            </Box>
          </Grid>

          <Grid item>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => addWallNode(wallIndex)} sx={{ backgroundColor: immersightButton }}>
              <FormattedMessage id="WallDrawer.addNode" />
            </Button>
          </Grid>

          {isCurrentWallShared() && (
            <Grid item>
              <Button variant="contained" startIcon={<DeleteIcon />} onClick={handleOnDelete} sx={{ backgroundColor: deleteButton }}>
                <FormattedMessage id="WallDrawer.deleteWall" />
              </Button>
            </Grid>
          )}
        </Grid>
      </Drawer>

      {dialogMode === 'MERGE' && mergeTargets && (
        <Dialog open={true} onClose={handleCancel} maxWidth="sm" fullWidth>
          <DialogTitle>
            <FormattedMessage id="WallDrawer.mergeRooms.title" defaultMessage="Räume zusammenführen" />
          </DialogTitle>

          <DialogContent dividers>
            <Typography gutterBottom>
              <FormattedMessage id="WallDrawer.mergeRooms.question" defaultMessage="Wie sollen die Räume vereint werden?" />
            </Typography>

            {mergeTargets && (
              <Stack spacing={2} mt={1}>
                <Button fullWidth variant="contained" color="primary" onClick={handleMergeIntoA} sx={{ backgroundColor: immersightButton }}>
                  {`${mergeTargets.b.name} (${mergeTargets.b.area.toFixed(2)} m²) in ${mergeTargets.a.name} (${mergeTargets.a.area.toFixed(2)} m²) erweitern`}
                </Button>

                <Button fullWidth variant="contained" color="primary" onClick={handleMergeIntoB} sx={{ backgroundColor: immersightButton }}>
                  {`${mergeTargets.a.name} (${mergeTargets.a.area.toFixed(2)} m²) in ${mergeTargets.b.name} (${mergeTargets.b.area.toFixed(2)} m²) erweitern`}
                </Button>
              </Stack>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCancel} variant="outlined" color="inherit">
              <FormattedMessage id="common.cancel" defaultMessage="Abbrechen" />
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {dialogMode === 'MERGE' && !mergeTargets && (
        <ConfirmationDialog open onClose={handleCancel} onConfirm={handleConfirm} titleId="ACHTUNG!" messageId="Räume werden zusammengeführt. Sind Sie sicher?" />
      )}
    </>
  );
};

export default StandardWallDrawer;
